import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatINR, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  CheckCircle2,
  Loader2,
  Crown,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const PLAN_FEATURES = {
  basic: [
    "Unlimited patients",
    "Appointment booking",
    "Basic billing & invoices",
    "1 doctor account",
    "Email support",
  ],
  pro: [
    "Everything in Basic",
    "Unlimited doctor accounts",
    "SMS appointment reminders",
    "Prescription image uploads",
    "Monthly revenue analytics",
    "Priority support",
  ],
};

export default function BillingUpgrade() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [pollState, setPollState] = useState(null); // null | "polling" | "paid" | "expired" | "error"

  useEffect(() => {
    (async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          api.get("/subscription/plans"),
          api.get("/subscription/status"),
        ]);
        setPlans(pRes.data);
        setStatus(sRes.data);
      } catch (e) {
        toast.error("Failed to load plans");
      } finally {
        setLoadingPlans(false);
      }
    })();
  }, []);

  // Poll on return from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPollState("paid");
          toast.success(`Subscription activated: ${data.plan_id?.toUpperCase()}`);
          // Refresh status
          const sRes = await api.get("/subscription/status");
          setStatus(sRes.data);
          // Clear param
          setTimeout(() => setParams({}), 2000);
          return;
        }
        if (data.status === "expired") {
          setPollState("expired");
          return;
        }
        if (attempts >= 8) {
          setPollState("error");
          return;
        }
        attempts++;
        timer = setTimeout(poll, 2000);
      } catch (e) {
        setPollState("error");
      }
    };
    setPollState("polling");
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [sessionId]);

  const handleSubscribe = async (plan_id) => {
    setSubmitting(plan_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
      setSubmitting(null);
    }
  };

  const handleRazorpay = async (plan_id) => {
    setSubmitting(`rzp-${plan_id}`);
    try {
      const { data } = await api.post("/payments/razorpay/order", { plan_id });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "HeDesk",
        description: `${data.plan_name} subscription (1 month)`,
        order_id: data.order_id,
        prefill: {
          name: data.user_name,
          email: data.user_email,
        },
        theme: { color: "#2563EB" },
        handler: async (response) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(`Subscription activated: ${plan_id.toUpperCase()}`);
            const sRes = await api.get("/subscription/status");
            setStatus(sRes.data);
          } catch (e2) {
            toast.error("Payment verification failed");
          } finally {
            setSubmitting(null);
          }
        },
        modal: {
          ondismiss: () => setSubmitting(null),
        },
      };
      if (!window.Razorpay) {
        toast.error("Razorpay not loaded — refresh the page");
        setSubmitting(null);
        return;
      }
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed");
        setSubmitting(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start Razorpay");
      setSubmitting(null);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto fade-in" data-testid="billing-upgrade-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">
          Subscription
        </div>
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Plans & billing
        </h1>
        <p className="text-slate-600 mt-1">Choose the plan that fits your clinic.</p>
      </div>

      {/* Polling banner */}
      {pollState === "polling" && (
        <div className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 text-sm flex items-center gap-3" data-testid="polling-banner">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment...
        </div>
      )}
      {pollState === "paid" && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm flex items-center gap-3" data-testid="paid-banner">
          <CheckCircle2 className="w-4 h-4" /> Payment confirmed. Your subscription is now active.
        </div>
      )}
      {pollState === "error" && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-sm flex items-center gap-3">
          <AlertCircle className="w-4 h-4" /> We couldn't confirm your payment automatically. Refresh in a minute or contact support.
        </div>
      )}
      {cancelled && !sessionId && (
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
          Checkout cancelled. You can pick a plan whenever you're ready.
        </div>
      )}

      {/* Current plan */}
      {status && (
        <Card className="border-slate-200 mb-8" data-testid="current-plan-card">
          <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Current plan</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>
                {status.plan === "trial" ? "Free Trial" : status.plan?.toUpperCase()}
              </div>
              {status.expires_at && (
                <div className="text-sm text-slate-500 mt-1">
                  {status.active ? "Renews" : "Expired"} on {new Date(status.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              )}
            </div>
            <Badge variant="outline" className={status.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
              {status.active ? "Active" : "Trial"}
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Plans */}
      {loadingPlans ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {plans.map((plan) => {
            const isPro = plan.plan_id === "pro";
            const isCurrent = status?.plan === plan.plan_id && status?.active;
            return (
              <Card
                key={plan.plan_id}
                className={`relative border-slate-200 ${isPro ? "border-blue-300 shadow-lg shadow-blue-500/10" : ""}`}
                data-testid={`plan-card-${plan.plan_id}`}
              >
                {isPro && (
                  <div className="absolute -top-3 right-6 bg-amber-400 text-amber-950 text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-2">
                    {isPro ? <Crown className="w-4 h-4 text-amber-500" /> : <Sparkles className="w-4 h-4 text-blue-500" />}
                    <span className="text-sm font-semibold uppercase tracking-wider text-slate-500">{plan.name}</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{formatINR(plan.amount)}</span>
                    <span className="text-slate-500 text-sm">/month</span>
                  </div>

                  <ul className="space-y-2.5 mb-6">
                    {(PLAN_FEATURES[plan.plan_id] || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <Button disabled className="w-full h-11" variant="outline">
                      Current plan
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Button
                        onClick={() => handleRazorpay(plan.plan_id)}
                        disabled={submitting === `rzp-${plan.plan_id}`}
                        className={`w-full h-11 ${isPro ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                        variant={isPro ? "default" : "outline"}
                        data-testid={`razorpay-${plan.plan_id}-btn`}
                      >
                        {submitting === `rzp-${plan.plan_id}` ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>Pay with Razorpay (UPI / Card)</>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleSubscribe(plan.plan_id)}
                        disabled={submitting === plan.plan_id}
                        variant="ghost"
                        size="sm"
                        className="w-full h-9 text-xs text-slate-500 hover:text-slate-700"
                        data-testid={`subscribe-${plan.plan_id}-btn`}
                      >
                        {submitting === plan.plan_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>or pay with Stripe (international)</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <p className="text-xs text-slate-400 text-center mt-8">
        Razorpay supports UPI, cards, netbanking, wallets. Stripe handles international cards. Cancel anytime.
      </p>
    </div>
  );
}
